import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, linkedSignal, signal } from '@angular/core';
import { AppShellResponsiveService } from './app-shell-responsive.service';

const STORAGE_KEY = 'sbe-shell-sidenav-collapsed';

/**
 * 셸 사이드바의 열림/접힘 상태 — 데스크톱 접기(완전 숨김, localStorage 영속)와 모바일
 * 오버레이(휘발성)를 분리해서 관리한다. 두 햄버거(툴바 안 / 콘텐츠 스트립 안)가 서로 다른
 * `@switch` 분기에 있어 컴포넌트가 아니라 서비스에 둔다 — `viewChild('drawer')` 같은
 * 명령형 참조 없이 시그널만으로 배선할 수 있다.
 *
 * `ThemeService`와 동일하게 localStorage 접근은 전부 try/catch로 감싼다(프라이빗
 * 브라우징 등에서 throw 가능 — web-preflight 가드는 이를 막지 못한다).
 */
@Injectable({ providedIn: 'root' })
export class ShellSidenavStateService {
  private readonly document = inject(DOCUMENT);
  private readonly isMobile = inject(AppShellResponsiveService).isMobile;

  /** 데스크톱 전용 접힘 상태 — 브레이크포인트가 바뀌어도 유지된다. */
  private readonly collapsedState = signal(this.readStored());
  /**
   * 모바일 오버레이 열림 — 브레이크포인트가 바뀔 때마다 닫힘으로 리셋한다. 이게 없으면
   * 모바일에서 열어둔 drawer가 다음번 모바일 복귀 때 오버레이로 되살아난다.
   */
  private readonly mobileOpenState = linkedSignal({ source: this.isMobile, computation: () => false });

  readonly mode = computed<'over' | 'side'>(() => (this.isMobile() ? 'over' : 'side'));
  readonly opened = computed(() => (this.isMobile() ? this.mobileOpenState() : !this.collapsedState()));

  toggle(): void {
    if (this.isMobile()) {
      this.mobileOpenState.update((open) => !open);
      return;
    }
    const next = !this.collapsedState();
    this.collapsedState.set(next);
    this.writeStored(next);
  }

  /** `MatSidenav`가 backdrop 클릭·ESC로 스스로 닫힐 때(openedChange) 상태를 되돌려 받는다. */
  onOpenedChange(open: boolean): void {
    if (this.isMobile()) {
      this.mobileOpenState.set(open);
      return;
    }
    this.collapsedState.set(!open);
    this.writeStored(!open);
  }

  /** 모바일에서 nav 항목을 클릭하면 오버레이 drawer를 닫는다. 데스크톱에서는 무해하게 무시된다. */
  closeOnNavigate(): void {
    if (this.isMobile()) this.mobileOpenState.set(false);
  }

  private readStored(): boolean {
    try {
      return this.document.defaultView?.localStorage?.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private writeStored(collapsed: boolean): void {
    try {
      this.document.defaultView?.localStorage?.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // 프라이빗 브라우징 등에서 저장을 못 해도 현재 세션의 토글 자체는 정상 동작해야 하므로 무시한다.
    }
  }
}
